import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AccessButton,
  BoxFull,
  Button,
  TabView,
  TransactionalSubheader,
} from "iuvity-ui-components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useUsers } from "../../hooks/useUsers";
import { AppContext } from "../../context/index";
import { REVALIDATE_FORM_PROPS, enabledMaster, functionMaster, infoDeletedUserFields, infoUsersDetailFields, locationMaster } from "../../constants";
import { ApplicationRolesComponent } from "../../components/users/ApplicationRolesComponent";
import { ManagementRolesComponent } from "../../components/users/ManagementRolesComponent";
import { UsersSchema } from "../../validations/schemas";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { Tag } from "iuvity-ui-components/dist/tag";
import { useUserRole } from "../../hooks/useUserRole";
import { useTenants } from "../../hooks/useTenants";
import { formatDateToLocale, formatStringToDate2 } from "../../helpers/formatDate";
import { PrivilegeValidatorComponent } from "../../components/shared/PrivilegeValidatorComponent";

interface DetailUserProps {
  update?: boolean;
};

const initFormBlankValues = {
  firstName: "",
  lastName: "",
  email: "",
  location: "",
  secondaryEmail: "",
  function: "",
  status: "",
  lastAccessDate: "",
};

export const DetailUserPage: React.FC<DetailUserProps> = ({
  update = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousUrl = location.state?.from ?? '';
  const {
    t,
    appState: { user, selectedUser, selectedApplicationRoles, selectedManagementRoles, selectedTenantId, tenantId, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues:
    {
      id: selectedUser?.id,
      firstName: selectedUser?.firstName,
      lastName: selectedUser?.lastName,
      email: selectedUser?.email,
      secondaryEmail: selectedUser?.secondaryEmail,
      status: selectedUser?.status,
      lastAccessDate: selectedUser?.lastAccessDate,
      deletedAt: selectedUser?.deletedAt,
      createdAt: selectedUser?.createdAt,
      function: Array.isArray(selectedUser?.attributes?.function) ? selectedUser?.attributes?.function[0] : selectedUser?.attributes?.function,
      location: Array.isArray(selectedUser?.attributes?.location) ? selectedUser?.attributes?.location[0] : selectedUser?.attributes?.location,
    }
  });

  const {
    getValues,
    formState: { isDirty, isValid },
  } = form;

  const formKeysOptions: any = {
    status: enabledMaster(t),
    location: locationMaster(t),
    function: functionMaster(t),
  };

  const { userAction } = useUsers("ID");
  const { dataRoles } = useUserRole("ROLES");
  const { data: dataRolePermissions } = useUserRole("DATA");
  const { listApps } = useTenants("LISTAPPS");
  const { listApps: listApps1 } = useTenants("LISTAPPS");

  const [activeIndex, setActiveIndex] = useState<number>(previousUrl === '/adminroles/detailroles?isadmin'
    ? 2 : (previousUrl === '/adminroles/detailroles' ? 1 : 0));
  const [rolesSelection, setRolesSelection] = useState(0);
  const [adminSelection, setAdminSelection] = useState(0);
  const [isButtonAppRoles, setIsButtonAppRoles] = useState<boolean>();
  const [isButtonAdminRoles, setIsButtonAdminRoles] = useState<boolean>();
  const [changeRoles, setChangeRoles] = useState<boolean>(false);




  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: null,
    }));
    navigate('/users');
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const { location, function: functionData, ...rest } = formValues;
    const data: any = {
      ...rest,
      tenantId: selectedTenantId || tenantId,
      attributes: { location: location, function: functionData }
    };

    // update roles
    const dataApplicationRoles = selectedApplicationRoles.map((rol: any) => {
      return {
        roleId: rol.roleId,
        appId: rol.appId,
        environmentId: rol.environmentId,
        tenantId: rol.tenantId,
        expeditionDate: rol.expeditionDate ? new Date(rol.expeditionDate) : null,
        expirationDate: rol.expirationDate ? new Date(rol.expirationDate) : null,

      }
    });

    const dataManagementRoles = selectedManagementRoles.map((rol: any) => {
      return {
        roleId: rol.id,
        appId: rol.appId,
        environmentId: rol.environmentId,
        tenantId: rol.tenantId,
        expeditionDate: rol.expeditionDate ? new Date(rol.expeditionDate) : null,
        expirationDate: rol.expirationDate ? new Date(rol.expirationDate) : null,
      }
    });

    data.roles = [...dataApplicationRoles, ...dataManagementRoles];
    const actionResult = await userAction("UPDATE", data);
    // const dataRolesResult = {
    //   userId: selectedUser?.id,
    //   roles: [...dataApplicationRoles, ...dataManagementRoles]
    // }

    // await userRoleAction("CREATE", dataRolesResult, selectedUser);

    if (actionResult === "SUCCESS") {
      navigate("/users");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Users.saveModalTitle`),
          content: t(`Users.saveModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Users.cancelModalTitle"),
          content: t("Users.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  const readOnlyObject = {
    firstName: update ? false : true,
    lastName: update ? false : true,
    email: update ? false : true,
    location: update ? false : true,
    secondaryEmail: update ? false : true,
    function: update ? false : true,
    status: update ? false : true,
    lastAccessDate: true,
    deletedAt: true,
    createdAt: true,

  };

  const actionDeleteFn = async (userId: any) => {
    await userAction("DELETE", userId);
    navigate("/users");
  };

  const handleDeleteModal = async (user?: any) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Users.deleteModalTitle"),
      content: `${t("Users.deleteUserModalContent1")} "${user.firstName} ${user.lastName}" ${t("Users.deleteUserModalContent2")}`,
      onConfirm: () => actionDeleteFn(user.id),
    }));
  };

  useEffect(() => {
    if (dataRolePermissions) {
      let data = dataRolePermissions[0]?.rolePermissions;
      const appRoles = data?.find((rol: any) => (rol?.functionality?.code === "COD_ADAPP" && rol.permission.privilegeId === "READ"));
      const adminRoles = data?.find((rol: any) => (rol?.functionality?.code === "COD_ROL" && rol.permission.privilegeId === "READ"));
      setIsButtonAppRoles(appRoles && true);
      setIsButtonAdminRoles(adminRoles && true);
    }
  }, [dataRolePermissions]);


  useMemo(() => {
    if (dataRoles) {
      const administrativeSelectedRoles = dataRoles
        .filter((rol: any) => {
          return rol?.role.isAdministrative === true
        })
        .map((userRole: any) => {
          return { ...userRole, id: userRole.roleId }
        });
      const applicationSelectedRoles = dataRoles
        .filter((rol: any) => {
          return rol?.role.isAdministrative === false
        })
        .map((userRole: any) => {
          return { ...userRole, id: `${userRole.environmentId}_${userRole.roleId}_${userRole.appId}` }
        });
      setAppState((prev) => ({
        ...prev,
        selectedManagementRoles: administrativeSelectedRoles
      }));
      setAppState((prev) => ({
        ...prev,
        selectedApplicationRoles: applicationSelectedRoles
      }));
    }
  }, [dataRoles])


  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div className="flex align-items-center" style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("Users.editTitle") : t("GlobalConfigurations.detailTitle")}
            <Tag
              className={'flow-badge ml-3'}
              value={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
              style={{
                textTransform: 'none',
                color: "var(--cardBackgroundColorOneShade80)"
              }}
            />
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Users.usersBreadcrum"),
              disabled: true
            },
            {
              label: t("Users.usersTitle"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              command: () => onReturn(),
            },
            {
              label: update ? t("Users.editTitle") : t("GlobalConfigurations.detailTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull className="my-4 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="mx-5">
          <div className="t-subheader-content" style={{ margin: "2.5rem 0 0" }}>
            <span
              dangerouslySetInnerHTML={{ __html: update ? t(`Users.usersEditDescription`) : t(`Users.usersDetailDescription`) }}
              style={{ lineHeight: 'var(--space-m)' }}
            />
          </div>
        </div>
        {(selectedUser.status !== 'DELETED' && !update) &&
          <div className="subheader-buttons">
            <PrivilegeValidatorComponent showButton={permissiondInfoByRole.UPDATE} >
              <AccessButton
                id='roles-edit-access-button'
                icon="iu iu-icon-edit"
                label={t("Users.editUserBtnLabel")}
                onClick={() => navigate("/users/edit")}
              />
            </PrivilegeValidatorComponent>
          </div>
        }
        <div style={{ marginLeft: '2.5rem' }}>
          <TabView
            className="upload-tabview mt-4"
            activeIndex={activeIndex}
            onTabChange={(e) => {
              setActiveIndex(e.index);
            }}
            items={[
              {
                key: "1",
                header: `${t("Users.basicInformation")}`,
                children: (
                  <CustomFormComponent
                    functionality="Users"
                    fields={selectedUser.status == 'DELETED' ? infoDeletedUserFields : infoUsersDetailFields}
                    form={form}
                    schemaOfValidation={UsersSchema}
                    dropdownOptions={formKeysOptions}
                    REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
                    readOnlyObject={readOnlyObject}
                    tooltipTextObject={{
                      email: t("Users.emailTooltip"),
                      secondaryEmail: t("Users.emailTooltip")
                    }}
                  />
                ),
                disabled: false,
              },
              {
                key: "2",
                header: `${t("Users.applicationRoles")} ( ${selectedApplicationRoles.length} )`,
                children: (
                  <ApplicationRolesComponent
                    data={listApps1}
                    rolesSelection={rolesSelection}
                    setRolesSelection={setRolesSelection}
                    isButtonAppRoles={isButtonAppRoles}
                    isButtonAdminRoles={isButtonAdminRoles}
                    isUpdate={true}
                    userRoleData={dataRoles}
                    onSelectRole={setChangeRoles}
                  />
                ),
                disabled: false,
              },
              {
                key: "3",
                header: `${t("Users.managementRoles")} ( ${selectedManagementRoles.length} )`,
                children: (
                  <ManagementRolesComponent
                    data={listApps}
                    adminSelection={adminSelection}
                    setAdminSelection={setAdminSelection}
                    isButtonAppRoles={isButtonAppRoles}
                    isButtonAdminRoles={isButtonAdminRoles}
                    isUpdate={true}
                    userRoleData={dataRoles}
                    onSelectRole={setChangeRoles}

                  />
                ),
                disabled: false,
              },
            ]}
          />
        </div>
      </BoxFull>




      {selectedUser.status !== 'DELETED' && <BoxFull className="mt-0 mb-3 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-4">
          <PrivilegeValidatorComponent showButton={!(!update && !permissiondInfoByRole.REMOVE)} >
            <Button
              variant="p-button-text"
              label={
                update
                  ? t("Users.cancelBtnLabel")
                  : t("Users.deleteBtnLabel")
              }
              onClick={update ? (isDirty ? () => handleModalProps("cancel") : onReturn) : () => handleDeleteModal(selectedUser)}
            />
          </PrivilegeValidatorComponent>
          <PrivilegeValidatorComponent showButton={!(!update && !permissiondInfoByRole.UPDATE)} >
            <Button
              variant="p-button"
              label={
                update
                  ? t("Users.saveChangesBtnLabel")
                  : t("Users.editUserBtnLabel")
              }
              disabled={
                update
                  ?
                  (!isValid || !(isDirty || changeRoles))
                  :
                  false
              }
              onClick={update ? () => handleModalProps('action') : () => navigate("/users/edit")}
            />
          </PrivilegeValidatorComponent>
        </div>
      </BoxFull>}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
